// ============================================================================
// SHARED EXPORT MODAL — used by all consultant tab components
// ============================================================================

import React, { useEffect, useState } from 'react';
import { FileText, FileDown, Code, X, Presentation } from 'lucide-react';
import { REPORT_THEME_OPTIONS, DEFAULT_REPORT_THEME } from '@process-advisor/process-miner/consultant/reportThemes';
import { getAppVersion } from '@process-advisor/process-miner/consultant/workbenchShared';

// Theme follows the app edition unless the caller overrides via defaultTheme:
// ServiceNow build ('SNOW') -> servicenow, Forge/Jira build -> atlassian.
const appDefaultReportTheme = () =>
  (getAppVersion() === 'SNOW' ? 'servicenow' : getAppVersion() ? 'atlassian' : DEFAULT_REPORT_THEME);

// Small brand swatch shown on each theme pill.
const THEME_SWATCH = {
  servicenow: ['#0C2F3F', '#00A13A'],
  atlassian:  ['#172B4D', '#1D7AFC'],
};

const ExportModal = ({ isOpen, onClose, title = 'Export', onExportHTML, onExportJSON, onExportPDF, onExportPPTX, defaultTheme }) => {
  const [theme, setTheme] = useState(() => defaultTheme || appDefaultReportTheme());

  useEffect(() => {
    if (isOpen) setTheme(defaultTheme || appDefaultReportTheme());
  }, [isOpen, defaultTheme]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const options = [
    {
      icon: <FileText size={22} />,
      label: 'Export as HTML',
      description: 'Standalone webpage — open in browser or share with stakeholders',
      onClick: onExportHTML,
      hoverBg:     '#E9F2FF',
      hoverBorder: '#85B8FF',
      hoverIcon:   '#0C66E4',
      hoverTitle:  '#0C66E4',
    },
    {
      icon: <FileDown size={22} />,
      label: 'Export as PDF',
      description: 'Print-ready document — opens print dialog for PDF save',
      onClick: onExportPDF,
      hoverBg:     '#FFEBE6',
      hoverBorder: '#D85A30',
      hoverIcon:   '#BF2600',
      hoverTitle:  '#BF2600',
    },
    ...(onExportPPTX ? [{
      icon: <Presentation size={22} />,
      label: 'Export as PowerPoint',
      description: 'Executive slide deck (.pptx) — summary, findings, recommendations and roadmap',
      onClick: onExportPPTX,
      hoverBg:     '#FBEED3',
      hoverBorder: '#F0D89B',
      hoverIcon:   '#C77414',
      hoverTitle:  '#C77414',
    }] : []),
    {
      icon: <Code size={22} />,
      label: 'Export as JSON',
      description: 'Raw structured data — for integration or further analysis',
      onClick: onExportJSON,
      hoverBg:     '#E6F1FB',
      hoverBorder: '#FF8B00',
      hoverIcon:   '#0052CC',
      hoverTitle:  '#0052CC',
    },
  ];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(9,30,66,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 10,
          width: 380,
          boxShadow: '0 8px 32px rgba(9,30,66,0.2)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          background: '#0C66E4',
          padding: '16px 20px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 30, height: 30, borderRadius: 7,
              background: 'rgba(255,255,255,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <FileText size={16} color="#fff" />
            </div>
            <span style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{title}</span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer',
              color: '#fff', padding: 5, borderRadius: 6,
              display: 'flex', alignItems: 'center',
            }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >
            <X size={16} />
          </button>
        </div>

        {/* Theme picker */}
        <div style={{ padding: '14px 16px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#6A7A86' }}>Theme</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {REPORT_THEME_OPTIONS.map(({ id, label }) => {
              const active = theme === id;
              const [dark, accent] = THEME_SWATCH[id] || ['#333', '#999'];
              return (
                <button
                  key={id}
                  onClick={() => setTheme(id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    padding: '5px 12px', borderRadius: 999, cursor: 'pointer',
                    fontSize: 12, fontWeight: 600,
                    border: active ? `1.5px solid ${dark}` : '1.5px solid #E5E9EB',
                    background: active ? dark : '#fff',
                    color: active ? '#fff' : '#41525F',
                  }}
                >
                  <span style={{ display: 'inline-flex' }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: active ? '#fff' : dark, marginRight: -3, border: '1px solid #fff' }} />
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: accent, border: '1px solid #fff' }} />
                  </span>
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Options */}
        <div style={{ padding: '12px 12px 16px' }}>
          {options.map(({ icon, label, description, onClick, hoverBg, hoverBorder, hoverIcon, hoverTitle }) => (
            <button
              key={label}
              onClick={() => { if (typeof onClick === 'function') onClick(theme); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 14,
                width: '100%', padding: '12px 14px',
                background: 'none',
                border: '1px solid #DFE1E6',
                borderRadius: 8,
                cursor: 'pointer',
                textAlign: 'left',
                marginBottom: 8,
                transition: 'background 0.15s, border-color 0.15s',
              }}
              onMouseOver={e => {
                e.currentTarget.style.background = hoverBg;
                e.currentTarget.style.borderColor = hoverBorder;
                e.currentTarget.querySelector('.opt-icon').style.color = hoverIcon;
                e.currentTarget.querySelector('.opt-title').style.color = hoverTitle;
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = 'none';
                e.currentTarget.style.borderColor = '#DFE1E6';
                e.currentTarget.querySelector('.opt-icon').style.color = '#42526E';
                e.currentTarget.querySelector('.opt-title').style.color = '#172B4D';
              }}
            >
              <span className="opt-icon" style={{ color: '#42526E', flexShrink: 0, transition: 'color 0.15s' }}>{icon}</span>
              <div>
                <div className="opt-title" style={{ fontSize: 14, fontWeight: 600, color: '#172B4D', transition: 'color 0.15s' }}>{label}</div>
                <div style={{ fontSize: 12, color: '#6B778C', marginTop: 2 }}>{description}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding: '10px 20px 14px',
          borderTop: '1px solid #EBECF0',
          display: 'flex', justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid #DFE1E6',
              borderRadius: 6, padding: '6px 16px',
              fontSize: 13, color: '#42526E', cursor: 'pointer',
            }}
            onMouseOver={e => { e.currentTarget.style.background = '#F4F5F7'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'none'; }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;